import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { createContext, useContext } from "react";
import { getErrorMessage } from "../../api/api-errors";
import {
  upsertProviderContactDetail,
  UpsertProviderContactDetailPayload,
} from "../../api/contactDetails.api";
import {
  markMedicalRecordRequestReadyToSend,
  regenerateMedicalRecordRequestPdf,
  sendMedicalRecordRequestReady,
  updateMedicalRecordRequest,
  UpdateMedicalRecordRequestPayload,
} from "../../api/medical-record-requests.api";
import { MedicalRecordRequest } from "../../api/medical-record-requests.type";
import { lookupProviderFax } from "../../api/providers.api";
import { useToast } from "../../hooks/ToastContext";
import {
  medicalRecordRequestsQueryKey,
  useMedicalRecordRequests,
} from "../hooks/useMedicalRecordRequests";

export type MedicalRecordRequestsContextValue = {
  requests: MedicalRecordRequest[];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => Promise<void>;
  markReadyToSend: (id: string) => Promise<MedicalRecordRequest>;
  isMarking: boolean;
  markingRequestId: string | null;
  sendRequest: (id: string) => Promise<MedicalRecordRequest>;
  isSending: boolean;
  sendingRequestId: string | null;
  updateRequest: (params: UpdateRequestParams) => Promise<MedicalRecordRequest>;
  isUpdating: boolean;
  updatingRequestId: string | null;
  updatingSource: UpdateRequestSource | null;
  lookupRecipientFax: (request: MedicalRecordRequest) => Promise<void>;
  isLookupLoading: boolean;
  lookupRequestId: string | null;
  upsertContactDetail: (params: UpsertContactParams) => Promise<void>;
  isSavingContact: boolean;
  savingContactRequestId: string | null;
  regeneratePdf: (requestId: string) => Promise<void>;
  isRegenerating: boolean;
  regeneratingRequestId: string | null;
};

type UpdateRequestSource = "edit" | "lookup" | string;

type UpdateRequestParams = {
  id: string;
  payload: UpdateMedicalRecordRequestPayload;
  successMessage?: string;
  source?: UpdateRequestSource;
};

export type ContactFormInput = {
  department: string;
  phone: string;
  fax: string;
  email: string;
  is_primary: boolean;
};

type UpsertContactParams = {
  request: MedicalRecordRequest;
  contact: ContactFormInput;
};

function resolveRecipientTarget(request: MedicalRecordRequest) {
  const normalizedType =
    request.recipientType ?? request.recipient_type ?? null;
  const hasInstitutionDetails = Boolean(
    request.recipientDetails?.institution?.id
  );
  const hasProviderDetails = Boolean(request.recipientDetails?.provider?.id);
  const isInstitutionRecipient =
    normalizedType === "institution" ||
    (!normalizedType && hasInstitutionDetails && !hasProviderDetails);
  const normalizedId =
    request.recipientExternalId ??
    request.recipient_external_id ??
    (isInstitutionRecipient
      ? request.recipientDetails?.institution?.id
      : request.recipientDetails?.provider?.id) ??
    null;

  return { normalizedId, isInstitutionRecipient };
}

export const MedicalRecordRequestsContext = createContext<
  MedicalRecordRequestsContextValue | undefined
>(undefined);

export function MedicalRecordRequestsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const queryClient = useQueryClient();
  const {
    data,
    isLoading,
    isError,
    error,
    refetch: queryRefetch,
  } = useMedicalRecordRequests();
  const { showToast } = useToast();

  const markReadyMutation = useMutation<MedicalRecordRequest, Error, string>({
    mutationFn: (id) => markMedicalRecordRequestReadyToSend(id),
    onSuccess: async (_, id) => {
      await queryClient.invalidateQueries({
        queryKey: medicalRecordRequestsQueryKey(),
      });
      await queryRefetch();
      showToast("Request marked as ready to send.", "success");
    },
    onError: (mutationError) => {
      showToast(getErrorMessage(mutationError), "error");
    },
  });

  const sendRequestMutation = useMutation<MedicalRecordRequest, Error, string>({
    mutationFn: (id) => sendMedicalRecordRequestReady(id),
    onSuccess: async (_, id) => {
      await queryClient.invalidateQueries({
        queryKey: medicalRecordRequestsQueryKey(),
      });
      await queryRefetch();
      showToast("Request sent", "success");
    },
    onError: (mutationError) => {
      showToast(getErrorMessage(mutationError), "error");
    },
  });

  const updateRequestMutation = useMutation<
    MedicalRecordRequest,
    Error,
    UpdateRequestParams
  >({
    mutationFn: ({ id, payload }) => updateMedicalRecordRequest(id, payload),
    onSuccess: async (_, { successMessage }) => {
      await queryClient.invalidateQueries({
        queryKey: medicalRecordRequestsQueryKey(),
      });
      await queryRefetch();
      showToast(successMessage ?? "Request updated", "success");
    },
    onError: (mutationError) => {
      showToast(getErrorMessage(mutationError), "error");
    },
  });

  const lookupFaxMutation = useMutation<
    { fax: string | null },
    Error,
    MedicalRecordRequest
  >({
    mutationFn: async (request) => {
      const { normalizedId, isInstitutionRecipient } =
        resolveRecipientTarget(request);
      if (!normalizedId) {
        throw new Error("Recipient ID is missing");
      }
      if (isInstitutionRecipient) {
        return lookupProviderFax({ institutionId: normalizedId });
      }
      return lookupProviderFax({ providerId: normalizedId });
    },
    onSuccess: async ({ fax }, request) => {
      if (fax) {
        await updateRequestMutation.mutateAsync({
          id: request.id,
          payload: { faxNumber: fax },
          successMessage: "Fax number updated",
          source: "lookup",
        });
      } else {
        showToast("No fax found for this recipient", "error");
      }
    },
    onError: (mutationError) => {
      showToast(getErrorMessage(mutationError), "error");
    },
  });

  const upsertContactMutation = useMutation<
    unknown,
    Error,
    UpsertContactParams
  >({
    mutationFn: ({ request, contact }) => {
      const { normalizedId, isInstitutionRecipient } =
        resolveRecipientTarget(request);
      const fallbackInstitutionId =
        (request.recipientDetails as any)?.institution?.id ??
        request.recipientExternalId ??
        request.recipient_external_id ??
        null;

      const payload: UpsertProviderContactDetailPayload = {
        department: contact.department || null,
        phone: contact.phone || null,
        fax: contact.fax || null,
        email: contact.email || null,
        is_primary: contact.is_primary,
        medical_record_request_id: request.id,
        ...(normalizedId
          ? isInstitutionRecipient
            ? { institution_id: normalizedId }
            : { provider_id: normalizedId }
          : fallbackInstitutionId
          ? { institution_id: fallbackInstitutionId }
          : {}),
      };
      return upsertProviderContactDetail(payload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: medicalRecordRequestsQueryKey(),
      });
      await queryRefetch();
      showToast("Contact saved", "success");
    },
    onError: (mutationError) => {
      showToast(getErrorMessage(mutationError), "error");
    },
  });

  const regeneratePdfMutation = useMutation<
    { pdfUrl: string | null },
    Error,
    string
  >({
    mutationFn: (requestId) => regenerateMedicalRecordRequestPdf(requestId),
    onSuccess: async (_, requestId) => {
      await queryClient.invalidateQueries({
        queryKey: medicalRecordRequestsQueryKey(),
      });
      await queryRefetch();
      showToast("PDF regenerated", "success");
    },
    onError: (mutationError) => {
      showToast(getErrorMessage(mutationError), "error");
    },
  });

  const value: MedicalRecordRequestsContextValue = {
    requests: data ?? [],
    isLoading,
    isError,
    error,
    refetch: async () => {
      await queryRefetch();
    },
    markReadyToSend: async (id: string) => {
      const result = await markReadyMutation.mutateAsync(id);
      return result;
    },
    isMarking: markReadyMutation.isPending,
    markingRequestId: markReadyMutation.variables ?? null,
    sendRequest: async (id: string) => {
      const result = await sendRequestMutation.mutateAsync(id);
      return result;
    },
    isSending: sendRequestMutation.isPending,
    sendingRequestId: sendRequestMutation.variables ?? null,
    updateRequest: async (params: UpdateRequestParams) => {
      return updateRequestMutation.mutateAsync(params);
    },
    isUpdating: updateRequestMutation.isPending,
    updatingRequestId: updateRequestMutation.variables?.id ?? null,
    updatingSource: updateRequestMutation.variables?.source ?? null,
    lookupRecipientFax: async (request: MedicalRecordRequest) => {
      await lookupFaxMutation.mutateAsync(request);
    },
    isLookupLoading: lookupFaxMutation.isPending,
    lookupRequestId: lookupFaxMutation.variables?.id ?? null,
    upsertContactDetail: async (params: UpsertContactParams) => {
      await upsertContactMutation.mutateAsync(params);
    },
    isSavingContact: upsertContactMutation.isPending,
    savingContactRequestId:
      upsertContactMutation.variables?.request?.id ?? null,
    regeneratePdf: async (requestId: string) => {
      await regeneratePdfMutation.mutateAsync(requestId);
    },
    isRegenerating: regeneratePdfMutation.isPending,
    regeneratingRequestId: regeneratePdfMutation.variables ?? null,
  };

  return (
    <MedicalRecordRequestsContext.Provider value={value}>
      {children}
    </MedicalRecordRequestsContext.Provider>
  );
}

export function useMedicalRecordRequestsContext() {
  const context = useContext(MedicalRecordRequestsContext);
  if (!context) {
    throw new Error(
      "useMedicalRecordRequestsContext must be used within MedicalRecordRequestsProvider"
    );
  }
  return context;
}
